// topbar-search.tsx — RECHERCHE TRANSVERSE du rail du haut.
//
// Le geste qu'elle sert : on tient un GLN lu sur un ticket, un n° de série lu sous une tablette, ou
// l'e-mail de quelqu'un au téléphone — et on ne sait pas encore si ça désigne un magasin, un appareil
// ou un compte. Chercher d'abord, choisir l'écran ensuite.
//
// ⚠️ Le PLACEHOLDER n'annonce que ce qu'on cherche VRAIMENT. Celui de la maquette promet cinq natures
// dont son propre code n'en filtre qu'une : une option offerte non implémentée (§23.0).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon, TopbarSearch, type IconName } from '@orgazone/ui-web';
import { api, type SearchResults } from './api';
import { deviceTitle } from './device-name';

/** Attente avant d'interroger le serveur : frappe au clavier ≠ intention de chercher. */
const DEBOUNCE_MS = 220;
const MIN_QUERY = 2;

/** Une ligne du panneau, mise à plat : le clavier parcourt UNE liste, pas quatre groupes imbriqués. */
interface Item { key: string; group: string; icon: IconName; label: string; detail: string; href: string }

/**
 * Met les résultats à plat dans l'ordre d'affichage. C'est ici que se décide OÙ MÈNE chaque nature :
 * un compte rattaché ouvre la fiche de son client, un compte staff l'écran des identités.
 */
function toItems(r: SearchResults): Item[] {
  return [
    ...r.tenants.map((t): Item => ({
      key: `t:${t.id}`, group: 'Clients', icon: 'building',
      label: t.name, detail: 'client', href: `/clients/${t.id}`,
    })),
    ...r.stores.map((s): Item => ({
      key: `s:${s.id}`, group: 'Magasins', icon: 'store',
      label: s.name, detail: [s.gln, s.sCode].filter(Boolean).join(' · '),
      href: `/clients/${s.tenantId}/magasin/${s.id}`,
    })),
    ...r.devices.map((d): Item => ({
      key: `d:${d.id}`, group: 'Appareils', icon: 'tablet',
      // Le nom qu'on lui a donné au parc, puis sa série : un appareil sans nom le DIT (« Sans nom »), sans
      // emprunter un autre champ — et un appareil sans série le dit aussi, c'est souvent celui qu'on cherche.
      label: deviceTitle(d),
      detail: [d.serial ?? 'n° de série absent', d.family, d.model, d.storeName].filter(Boolean).join(' · '),
      // ⚠️ La flotte n'a pas de route par appareil : sans repère, on retombait sur une liste entière où
      // il fallait re-chercher à l'œil ce qu'on venait de trouver. L'identifiant passe donc en query,
      // et l'écran surligne la ligne — c'est le résultat de la recherche qui doit sauter aux yeux.
      href: `/appareils?device=${encodeURIComponent(d.id)}`,
    })),
    ...r.accounts.map((a): Item => ({
      key: `a:${a.email}`, group: 'Comptes', icon: 'user',
      label: a.name ?? a.email,
      detail: [a.email, a.tenantName ?? (a.tenantId === null ? 'Orgazone' : 'non rattaché')].join(' · '),
      href: a.tenantId ? `/clients/${a.tenantId}` : '/staff',
    })),
  ];
}

/**
 * ⚠️ RENOMMÉE DE `TopbarSearch` EN `GlobalSearch` : la charte expose désormais un
 * `TopbarSearch` — le CHAMP —, et ce composant-ci est la RECHERCHE TRANSVERSE, c'est-à-dire le champ
 * PLUS son panneau, son clavier et son débounce. Deux composants homonymes dont l'un enveloppe l'autre
 * ne se distinguent à la lecture par rien, et l'outil de renommage au parseur ne peut pas les départager
 * (il renomme un NOM dans une zone). Le nom dit donc ce que la chose fait de plus que le champ.
 */
export function GlobalSearch() {
  const [q, setQ] = useState('');
  const [res, setRes] = useState<SearchResults | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const boite = useRef<HTMLDivElement>(null);
  const champ = useRef<HTMLInputElement>(null);
  // ⚠️ Garde anti-RÉPONSE PÉRIMÉE : deux frappes rapides lancent deux appels, et rien ne garantit
  // qu'ils reviennent dans l'ordre. Sans ce jeton, une réponse lente écrase une réponse récente et
  // le panneau affiche les résultats d'une saisie que l'utilisateur a déjà corrigée.
  const dernier = useRef(0);
  const nav = useNavigate();
  const { pathname } = useLocation();

  const items = useMemo(() => (res ? toItems(res) : []), [res]);

  useEffect(() => {
    const brut = q.trim();
    if (brut.length < MIN_QUERY) { setRes(null); setErr(null); setBusy(false); return; }
    const jeton = ++dernier.current;
    setBusy(true);
    const t = setTimeout(() => {
      api.search(brut)
        .then((r) => { if (jeton === dernier.current) { setRes(r); setErr(null); setCursor(0); } })
        .catch((e) => { if (jeton === dernier.current) { setRes(null); setErr((e as Error).message); } })
        .finally(() => { if (jeton === dernier.current) setBusy(false); });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  // Un changement d'écran ferme le panneau : le laisser ouvert sur la page d'arrivée n'aurait plus de sens.
  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    const dehors = (e: MouseEvent) => { if (!boite.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', dehors);
    return () => document.removeEventListener('mousedown', dehors);
  }, []);

  const aller = useCallback((it: Item) => {
    setOpen(false);
    setQ('');
    champ.current?.blur();
    nav(it.href);
  }, [nav]);

  const clavier = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); champ.current?.blur(); return; }
    if (!items.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setCursor((c) => (c + 1) % items.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setOpen(true); setCursor((c) => (c - 1 + items.length) % items.length); }
    else if (e.key === 'Enter') { e.preventDefault(); const it = items[cursor]; if (it) aller(it); }
  };

  const assezLong = q.trim().length >= MIN_QUERY;
  const deploye = open && assezLong;
  // Un groupe n'apparaît qu'avec ses lignes ; l'en-tête se pose au premier item de chaque groupe.
  let groupePose = '';

  return (
    /* ⚠️ 🔴 LE CHAMP VIENT DE LA CHARTE (`TopbarSearch`) ET N'EST PLUS DESSINÉ ICI : cet
       écran portait sa propre géométrie (34 px, `--r-sm`, fond `--canvas`), identique à celle de la
       console EDI — identique par COÏNCIDENCE, pas par partage. Le dessin EDI demandant
       une pilule, corriger deux fois aurait gravé deux aspects pour un même geste.
       ⚠️ Ce qui reste ICI est ce qui n'appartient qu'à la Console : le panneau de résultats, son
       clavier et son débounce. La pilule n'héberge que le champ et le conteneur positionné. */
    <TopbarSearch
      boxRef={boite}
      inputRef={champ}
      value={q}
      onChange={(v) => { setQ(v); setOpen(true); }}
      onFocus={() => setOpen(true)}
      onKeyDown={clavier}
      placeholder="Rechercher : client, magasin, GLN, n° de série, e-mail"
      label="Rechercher un client, un magasin, un appareil ou un compte"
    >
      {deploye && (
        <div className="ozin" style={{
          position: 'absolute', top: 40, left: 0, width: 'min(420px, 92vw)', zIndex: 40,
          background: 'color-mix(in srgb, var(--elev, var(--paper)) 96%, transparent)', backdropFilter: 'var(--glass-blur)',
          border: '1px solid var(--ink200)', borderRadius: 'var(--r-md)', boxShadow: '0 20px 50px -18px rgba(0,0,0,.6)',
          padding: 6, maxHeight: '70vh', overflowY: 'auto',
        }}>
          {/* ⚠️ Chaque état se DIT. « Aucun résultat » ne doit jamais couvrir une recherche qui a
              échoué, ni une recherche encore en cours : ce sont trois choses différentes. */}
          {err ? (
            <div style={{ padding: '10px 12px', fontSize: 'var(--t-dense)', color: 'var(--da-fg)' }}>Recherche indisponible : {err}</div>
          ) : busy && !res ? (
            <div style={{ padding: '10px 12px', fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>Recherche…</div>
          ) : (
            <>
              {items.map((it, i) => {
                const header = it.group !== groupePose ? (groupePose = it.group) : null;
                return (
                  <div key={it.key}>
                    {header && (
                      <div className="mono" style={{ padding: '8px 10px 4px', fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>{header}</div>
                    )}
                    <button
                      type="button"
                      onMouseEnter={() => setCursor(i)}
                      onClick={() => aller(it)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 9, width: '100%', textAlign: 'left',
                        padding: '7px 10px', borderRadius: 'var(--r-sm)', border: 'none', background: i === cursor ? 'var(--ink100)' : 'transparent',
                      }}
                    >
                      <Icon name={it.icon} size={14} color="var(--ink400)" />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
                        <span className="mono" style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.detail}</span>
                      </span>
                    </button>
                  </div>
                );
              })}

              {/* L'IdP muet : le groupe « comptes » n'est pas VIDE, il est INCONNU — et ça se dit. */}
              {res?.accountsError && (
                <div style={{ padding: '8px 10px', fontSize: 'var(--t-note)', color: 'var(--wa-fg)', borderTop: items.length ? '1px solid var(--ink200)' : undefined, marginTop: items.length ? 4 : 0 }}>
                  Comptes non consultés — l’IdP n’a pas répondu : {res.accountsError}
                </div>
              )}

              {res && !items.length && !res.accountsError && (
                <div style={{ padding: '10px 12px', fontSize: 'var(--t-dense)', color: 'var(--ink500)' }}>Aucun résultat pour « {res.query} »</div>
              )}
            </>
          )}
        </div>
      )}
    </TopbarSearch>
  );
}
